import { useRef } from 'react';
import type { DropOperation, DropTarget, Key } from '@react-types/shared';
import { useDragAndDrop } from 'react-aria-components';
import {
  areEqualKeys,
  moveKeysAfter,
  moveKeysBefore
} from 'v5/patterns/dnd/collectionReorder';
import type { DndListRenderDropIndicator } from 'v5/patterns/dnd/DndListDropIndicator';

export type CollectionReorderDragItem = { 'text/plain': string };

export type UseCollectionReorderOptions<TKey extends Key> = {
  orderedKeys: TKey[];
  onReorder?: (next: TKey[]) => void;
  textByKey: Map<string, string>;
  renderDropIndicator: DndListRenderDropIndicator;
  enabled?: boolean;
  getItems?: (keys: Iterable<Key>) => CollectionReorderDragItem[];
  getAllowedDropOperations?: () => DropOperation[];
  getDropOperation?: (target: DropTarget) => DropOperation;
};

export function useCollectionReorder<TKey extends Key>({
  orderedKeys,
  onReorder,
  textByKey,
  renderDropIndicator,
  enabled = true,
  getItems: getItemsProp,
  getAllowedDropOperations: getAllowedDropOperationsProp,
  getDropOperation: getDropOperationProp
}: UseCollectionReorderOptions<TKey>) {
  const orderedKeysRef = useRef(orderedKeys);
  orderedKeysRef.current = orderedKeys;

  const { dragAndDropHooks } = useDragAndDrop({
    getItems(keys) {
      if (getItemsProp) {
        return getItemsProp(keys);
      }
      return Array.from(keys).map(key => ({
        'text/plain': textByKey.get(String(key)) ?? String(key)
      }));
    },
    renderDropIndicator,
    getAllowedDropOperations:
      getAllowedDropOperationsProp ?? (() => (enabled ? ['move'] : [])),
    getDropOperation: getDropOperationProp ?? (() => 'move'),
    onReorder(e) {
      if (!enabled || !onReorder) {
        return;
      }
      const targetKey = e.target.key as TKey;
      const current = orderedKeysRef.current;
      const next =
        e.target.dropPosition === 'before'
          ? moveKeysBefore(current, targetKey, e.keys)
          : e.target.dropPosition === 'after'
            ? moveKeysAfter(current, targetKey, e.keys)
            : current;
      if (!areEqualKeys(next, current)) {
        onReorder(next);
      }
    }
  });

  return { dragAndDropHooks };
}
